/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback } from 'react';

interface UseCheckboxesProps<T, C> {
  items: T[];
  compareChecks?: (a: C, b: C) => boolean;
  isItemChecked: (item: T, check: C) => boolean;
  createCheckItem: (item: T) => C;
}

export const useCheckboxes = <T extends object, C>({
  items,
  compareChecks = (a, b) => String(a) === String(b),
  isItemChecked,
  createCheckItem,
}: UseCheckboxesProps<T, C>) => {
  const [checks, setChecks] = useState<C[]>([]);

  const isAlreadyChecked = (check: C, checks: C[]): boolean =>
    checks.some(checkedItem => compareChecks(checkedItem, check));

  const handleCheck = useCallback(
    (item: C) => {
      setChecks(prevChecks => {
        if (isAlreadyChecked(item, prevChecks)) {
          return prevChecks.filter(
            checkedItem => !compareChecks(item, checkedItem)
          );
        }
        return [...prevChecks, item];
      });
    },
    [setChecks]
  );

  const handleAllCheck = (isAllChecked: boolean) => {
    setChecks(prevChecks => {
      if (isAllChecked) {
        return prevChecks.filter(
          checkedItem => !items.some(item => isItemChecked(item, checkedItem))
        );
      }

      return items.reduce(
        (newChecks, item) => {
          const newCheck = createCheckItem(item);
          if (!isAlreadyChecked(newCheck, newChecks)) {
            newChecks.push(newCheck);
          }
          return newChecks;
        },
        [...prevChecks]
      );
    });
  };

  const isChecked = useCallback((check: C) => isAlreadyChecked(check, checks), [
    checks,
  ]);

  const clearChecks = () => setChecks([]);

  return {
    checks,
    clearChecks,
    setChecks,
    handleCheck,
    handleAllCheck,
    isChecked,
  };
};
